import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CxoneAwardService } from './cxone-award.service';
import OverlayScrollbars from 'overlayscrollbars';
import { AwardData, ContextAwardItem, FolderedAward } from './cxone-award.model';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'cv-cxone-award-modal',
    templateUrl: './cxone-award-modal.component.html',
    styleUrl: './cxone-award-modal.component.scss'
})
export class CxoneAwardModalComponent implements OnInit {
    @Input() selectedRow: any;
    @Input() moduleKey: string;
    @Input() groupID: number;
    @Input() modalOpen: boolean;
    @Input() dashKey: string;
    @Input() isCxOne: boolean = true;
    @Input() isFromDashboard: boolean = true;    

    groupLevel: number = 1;
    folders: FolderedAward[] = [];
    isLoading: boolean = true;
    foldersKeyValues: string;
    searchValue: string = "";
    filteredFolders: FolderedAward[] = [];
    disableButton: boolean = true;
    selectButton: boolean = false;
    selectedAward: ContextAwardItem | null = null;
    confirmationMessage: string = '';
    isGivingAward: boolean = false;

    constructor(
        private activeModal: NgbActiveModal,
        private decimalPipe: DecimalPipe,
        private cvLocalizationService: CVLocalizationService,
        private awardService: CxoneAwardService,
    ) { }

    async ngOnInit() {
        const groupData = {
            groupID: this.groupID,
            groupLevel: this.groupLevel
        };
    
        try {
            this.filteredFolders = this.folders = (await this.awardService.loadAvailableAwards(this.moduleKey, groupData))?.folderedAwards ?? [];
            this.foldersKeyValues = Array.from({ length: this.folders.length }, (_, index) => index.toString()).join(", ");
        } catch (error) {
            console.error('Failed to load available awards', error);
            this.filteredFolders = this.folders = [];
        } finally {
            this.isLoading = false;
        }
    
        // Select first award by default
        setTimeout(() => {
            if (this.filteredFolders.length > 0 && this.filteredFolders[0].Value.length > 0) {
                this.saveSelectedAward(this.filteredFolders[0].Value[0]);
            }
        });
    }

    close() {
        this.activeModal.close();
    }

    scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "accordion-scrollbar"
        }
    };

    getLocalizationString(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText("Manual_Awards", key, defaultValue);
    }

    filterByRuleName(): void {
        let selectedAwardFound = false;
        if (this.searchValue.length === 0) {
            this.filteredFolders = this.folders;
            selectedAwardFound = !!this.selectedAward;
        } else {
            this.filteredFolders = this.folders.reduce((accFolders, folder) => {
                const filteredAwards = folder.Value.filter(award => award.rule.RuleName.toLowerCase().includes(this.searchValue.toLowerCase()));
                if (filteredAwards.length != 0) {
                    accFolders.push({
                        Key: folder.Key,
                        Value: filteredAwards
                    });
                }
                if (filteredAwards.some(award => award.rule.RuleID === this.selectedAward?.rule.RuleID)) {
                    selectedAwardFound = true;
                }
                return accFolders;
            }, []);
        }
        this.disableButton = !selectedAwardFound || !this.selectedAward;
        this.foldersKeyValues = Array.from({ length: this.filteredFolders.length }, (_, index) => index.toString()).join(", ");
    }

    saveSelectedAward(award: ContextAwardItem): void {
        this.selectedAward = award;
        this.updateConfirmationMessage();
        this.disableButton = !award;
    }

    isSelected(award: ContextAwardItem): boolean {
        return this.selectedAward === award;
    }

    formatCoinAmount(value: number | null): string {
        if (value === null || value === undefined) {
            return '';
        }
        return this.decimalPipe.transform(parseFloat(value.toString()), undefined, CVCultureName) || '';
    }

    async giveAward() {
        //Validation on top to avoid delay between the frontend and function call to give an award
        if (this.isGivingAward) {
            return;
        }

        this.isGivingAward = true;
        //Validation to enable the confirmation button if no award is selected
        if (!this.selectedAward) {
            this.isGivingAward = false;
            return;
        }

        try {
            await this.awardService.giveAward(
                this.selectedAward.rule.RuleID,
                {
                    groupID: this.groupID,
                    groupLevel: this.groupLevel
                },
                this.isFromDashboard,
                this.isCxOne
            );
            this.activeModal.close();
        } catch (error) {
            console.error("Failed to give award");
            this.isGivingAward = false;
        }
    }

    updateConfirmationMessage(): void {
        if (this.selectedAward) {
            const template = this.getLocalizationString("Confirmation_Award_Message", "Using {0} coins to award. Proceed?");
            this.confirmationMessage = template.replace("{0}", this.formatCoinAmount(this.selectedAward.coinAmount));
        }
    }
}